import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Alert from "../common/Alert";
import Button from "../common/Button";
import Input from "../common/Input";
import LogoutConfirmationModal from "../common/LogoutConfirmationModal";
import Modal from "../common/Modal";
import NumericKeypadModal from "../common/NumericKeypadModal";
import {
  useGetOpenTillSessionsQuery,
  useOpenTillSessionMutation,
} from "../../store/api";
import { useStore } from "../../context/StoreContext";
import {
  normalizeDeviceName,
  useDeviceCashSessionResolution,
} from "../../hooks/useDeviceCashSession";

const todayLocal = () => {
  const date = new Date();
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const getErrorMessage = (err, fallback) =>
  err?.data?.detail ||
  err?.data?.message ||
  err?.message ||
  fallback ||
  "Something went wrong.";

const CashSessionStartupModal = ({
  autoPrompt = true,
  isOpenRequested = false,
  onRequestClose,
  onSessionStarted,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedStoreId } = useStore();

  const [openingCash, setOpeningCash] = useState("0");
  const [businessDate, setBusinessDate] = useState(todayLocal());
  const [errorMessage, setErrorMessage] = useState("");
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [autoPromptDismissed, setAutoPromptDismissed] = useState(false);

  const {
    data: openSessionsData,
    isLoading: openSessionsLoading,
    isError: openSessionsIsError,
    error: openSessionsError,
  } = useGetOpenTillSessionsQuery(selectedStoreId, { skip: !selectedStoreId });

  const [openTillSession, openState] = useOpenTillSessionMutation();

  const openSessions = useMemo(
    () => (Array.isArray(openSessionsData) ? openSessionsData : []),
    [openSessionsData],
  );
  const deviceSessionResolution = useDeviceCashSessionResolution(
    openSessions,
    Boolean(selectedStoreId),
  );
  const deviceName = normalizeDeviceName(
    localStorage.getItem("edgeDeviceToken") ||
      deviceSessionResolution.device?.deviceKey ||
      deviceSessionResolution.device?.deviceName ||
      localStorage.getItem("edgeDeviceName") ||
      process.env.REACT_APP_DEVICE_TOKEN ||
      process.env.REACT_APP_DEVICE_NAME ||
      "",
  );
  const hasOpenSession = deviceSessionResolution.status === "ready";
  const isReady = Boolean(selectedStoreId) && !openSessionsLoading;
  const shouldPrompt =
    isReady &&
    !hasOpenSession &&
    (isOpenRequested || (autoPrompt && !autoPromptDismissed));
  const resolvedTillName = deviceName || "Unknown device";

  useEffect(() => {
    if (!selectedStoreId) {
      setOpeningCash("0");
      setBusinessDate(todayLocal());
      setErrorMessage("");
      setKeyboardOpen(false);
      setAutoPromptDismissed(false);
      return;
    }
    setAutoPromptDismissed(false);
  }, [selectedStoreId]);

  const handleOpenSession = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!resolvedTillName.trim()) {
      setErrorMessage("Device token is unavailable.");
      return;
    }

    if (openingCash === "" || Number.isNaN(Number(openingCash))) {
      setErrorMessage("Opening cash must be a valid number.");
      return;
    }

    try {
      await openTillSession({
        payload: {
          openingCash: Number(openingCash),
          deviceName: normalizeDeviceName(resolvedTillName),
          businessDate,
        },
      }).unwrap();

      setOpeningCash("0");
      setErrorMessage("");
      setKeyboardOpen(false);
      setAutoPromptDismissed(false);
      onSessionStarted?.();

      if (location.pathname === "/pos" || location.pathname.startsWith("/pos/cash")) {
        navigate("/pos/cash-movements", { replace: true });
      }
    } catch (err) {
      setErrorMessage(getErrorMessage(err, "Failed to open till session."));
    }
  };

  const requestLogoutConfirmation = () => {
    setKeyboardOpen(false);
    setLogoutConfirmOpen(true);
  };

  const requestCloseConfirmation = () => {
    if (openState.isLoading) return;
    setKeyboardOpen(false);
    setCloseConfirmOpen(true);
  };

  const confirmClosePrompt = () => {
    setCloseConfirmOpen(false);
    onRequestClose?.();
    setAutoPromptDismissed(true);
  };

  return (
    <>
      <Modal
        isOpen={shouldPrompt}
        onClose={requestCloseConfirmation}
        title="Start Till Session"
        confirmText={openState.isLoading ? "Starting..." : "Start Session"}
        onConfirm={handleOpenSession}
        isLoading={openState.isLoading}
        size="lg"
        align="center"
        showCancel={false}
        showCloseButton
        footerStart={
          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={requestLogoutConfirmation}
            disabled={openState.isLoading}
          >
            Logout
          </Button>
        }
      >
        <div className="space-y-4">
          {!openingCash.trim() && (
            <Alert tone="warning">
              Enter the amount currently in the drawer so the session starts
              from the correct opening cash.
            </Alert>
          )}

          {openSessionsIsError && (
            <Alert tone="error">
              {errorMessage ||
                getErrorMessage(
                  openSessionsError,
                  "Failed to prepare the session start.",
                )}
            </Alert>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Till Name"
              value={resolvedTillName}
              readOnly
            />

            <Input
              label="Opening Cash"
              type="number"
              value={openingCash}
              onChange={(e) => setOpeningCash(e.target.value)}
              onFocus={() => setKeyboardOpen(true)}
              onClick={() => setKeyboardOpen(true)}
              placeholder="0.00"
              required
            />
          </div>

          {/* Business Date is currently hidden from the UI but still submitted */}
          {/*
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Business Date"
              type="date"
              value={businessDate}
              min={todayLocal()}
              max={todayLocal()}
              onChange={(e) => setBusinessDate(e.target.value)}
              required
            />
          </div>
          */}
        </div>
      </Modal>

      <Modal
        isOpen={closeConfirmOpen}
        onClose={() => setCloseConfirmOpen(false)}
        title="Close Start Session"
        message="Are you sure you want to close this dialog? The till session has not been started yet."
        confirmText="Close"
        cancelText="Keep Open"
        onConfirm={confirmClosePrompt}
        size="md"
        align="center"
        zIndexClassName="z-[60]"
        showCancel
        showCloseButton
      />

      <NumericKeypadModal
        isOpen={keyboardOpen && shouldPrompt}
        title="Opening Cash"
        value={openingCash}
        onChange={(value) => setOpeningCash(value)}
        onClose={() => setKeyboardOpen(false)}
        allowDecimal
      />

      <LogoutConfirmationModal
        isOpen={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
        message="Are you sure you want to logout? The till session has not been started yet."
      />
    </>
  );
};

export default CashSessionStartupModal;
